import type { ToolRunner } from './base.js';
import type { CheckResult } from '../types/index.js';
import { checkCommand, runCommand, parseSeverity } from './base.js';

interface SemgrepResult {
  results: Array<{
    check_id: string;
    path: string;
    start: { line: number; col: number };
    end: { line: number; col: number };
    extra: {
      message: string;
      severity: string;
      metadata?: {
        category?: string;
        cwe?: string[];
        owasp?: string[];
      };
      fix?: string;
    };
  }>;
  errors: Array<{
    message: string;
    level: string;
  }>;
}

export const semgrepRunner: ToolRunner = {
  name: 'semgrep',

  async isInstalled(): Promise<boolean> {
    return checkCommand('semgrep');
  },

  async getVersion(): Promise<string | null> {
    const result = await runCommand('semgrep', ['--version'], process.cwd());
    if (result.exitCode === 0) {
      return result.stdout.trim();
    }
    return null;
  },

  async run(cwd: string, files?: string[]): Promise<CheckResult[]> {
    const args = [
      'scan',
      '--json',
      '--config',
      'auto',
      '--metrics',
      'off',
    ];

    if (files && files.length > 0) {
      args.push(...files);
    } else {
      args.push(cwd);
    }

    const result = await runCommand('semgrep', args, cwd);

    try {
      const output: SemgrepResult = JSON.parse(result.stdout);
      const results: CheckResult[] = [];

      for (const finding of output.results) {
        results.push({
          ruleId: finding.check_id,
          severity: parseSeverity(finding.extra.severity),
          message: finding.extra.message,
          file: finding.path,
          line: finding.start.line,
          column: finding.start.col,
          endLine: finding.end.line,
          endColumn: finding.end.col,
          fix: finding.extra.fix
            ? { description: 'Apply suggested fix', replacement: finding.extra.fix }
            : undefined,
        });
      }

      return results;
    } catch {
      if (result.exitCode === 0) {
        return [];
      }
      return [
        {
          ruleId: 'semgrep.error',
          severity: 'error',
          message: `Semgrep error: ${result.stderr || 'Unknown error'}`,
        },
      ];
    }
  },
};
